import { useLanguage } from '../context/LanguageContext';
import { Pencil, Circle, Cpu, Wrench } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';

const PRODUCTION_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/n2sltbd7_WhatsApp%20Image%202024-11-17%20at%2021.04.45%20%281%29.jpeg';
const WELDING_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/3wifgtrh_WhatsApp%20Image%202024-11-17%20at%2021.04.50.jpeg';
const WELDING2_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/gfe4xo80_WhatsApp%20Image%202024-11-17%20at%2021.04.49%20%281%29.jpeg';
const WELDING3_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/ju8kbuwj_1746633956430.jpg';
const BLUEPRINT_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/gp3etg9k_S3-15%20MVA%2020-13%2C8%20KV%20astravolt%20tasar%C4%B1m-Model.jpg';

export default function Production() {
  const { t } = useLanguage();

  const steps = [
    { key: 'design', icon: <Pencil className="w-6 h-6" />, img: PRODUCTION_IMG, num: '01' },
    { key: 'core', icon: <Circle className="w-6 h-6" />, img: WELDING_IMG, num: '02' },
    { key: 'windings', icon: <Cpu className="w-6 h-6" />, img: WELDING2_IMG, num: '03' },
    { key: 'assembly', icon: <Wrench className="w-6 h-6" />, img: WELDING3_IMG, num: '04' },
  ];

  return (
    <div data-testid="production-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#05060A] grid-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-3">Astravolt</p>
          <h1 data-testid="production-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white">
            {t('production_section.label')}
          </h1>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-0">
            {/* DESIGN STEP - Special layout with blueprint */}
            <Reveal>
              <div className="py-16 md:py-20 border-b border-white/5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center mb-12">
                  <div className="relative overflow-hidden rounded-sm">
                    <img
                      src={steps[0].img}
                      alt={t('production_section.design.title')}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/40 to-transparent" />
                    <div className="absolute top-6 left-6">
                      <span className="text-5xl font-bold text-white/10">01</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400">
                        {steps[0].icon}
                      </div>
                      <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium">
                        {t('production_section.label')}
                      </p>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
                      {t('production_section.design.title')}
                    </h2>
                    <p className="text-base text-slate-300 leading-relaxed">
                      {t('production_section.design.desc')}
                    </p>
                  </div>
                </div>

                {/* Blueprint Section */}
                <div
                  data-testid="blueprint-section"
                  className="relative rounded-sm overflow-hidden border border-blue-500/20 bg-[#050a18]"
                  style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.08), inset 0 0 60px rgba(59, 130, 246, 0.03)' }}
                >
                  {/* Grid overlay */}
                  <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                      backgroundImage: 'linear-gradient(rgba(59,130,246,1) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,1) 1px, transparent 1px)',
                      backgroundSize: '40px 40px',
                    }}
                  />

                  {/* Top bar */}
                  <div className="relative flex items-center justify-between px-6 py-3 border-b border-blue-500/15 bg-blue-600/5">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                      <span className="text-xs font-bold text-blue-400 uppercase tracking-[0.2em]">
                        Technical Drawing - CAD View
                      </span>
                    </div>
                    <span className="text-xs text-blue-400/60 font-mono">S3-15 MVA 20-13,8 KV / 7,5 MVA - 33/11 KV</span>
                  </div>

                  {/* Blueprint image */}
                  <div className="relative p-4 md:p-8">
                    <img
                      src={BLUEPRINT_IMG}
                      alt="S3-15 MVA 20-13,8 KV Astravolt Tasarim"
                      className="w-full h-auto max-h-[600px] object-contain mx-auto rounded-sm"
                      style={{
                        filter: 'invert(1) hue-rotate(180deg) brightness(0.85) contrast(1.1)',
                      }}
                    />
                  </div>

                  {/* Bottom bar */}
                  <div className="relative flex items-center justify-between px-6 py-3 border-t border-blue-500/15 bg-blue-600/5">
                    <div className="flex items-center gap-6">
                      <span className="text-[10px] text-blue-400/50 font-mono uppercase">Scale: 1:25</span>
                      <span className="text-[10px] text-blue-400/50 font-mono uppercase">Units: mm</span>
                      <span className="text-[10px] text-blue-400/50 font-mono uppercase">Projection: 3rd Angle</span>
                    </div>
                    <span className="text-[10px] text-blue-400/50 font-mono">ASTRAVOLT TRANSFORMER ENG. DEPT.</span>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Remaining steps (core, windings, assembly) */}
            {steps.slice(1).map((step, i) => (
              <Reveal key={step.key}>
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center py-16 md:py-20 ${i < steps.length - 2 ? 'border-b border-white/5' : ''}`}>
                  <div className={`relative overflow-hidden rounded-sm ${i % 2 === 0 ? 'md:order-2' : ''}`}>
                    <img
                      src={step.img}
                      alt={t(`production_section.${step.key}.title`)}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/40 to-transparent" />
                    <div className="absolute top-6 left-6 flex items-center gap-3">
                      <span className="text-5xl font-bold text-white/10">{step.num}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={i % 2 === 0 ? 'md:order-1' : ''}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400">
                        {step.icon}
                      </div>
                      <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium">
                        {t('production_section.label')}
                      </p>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
                      {t(`production_section.${step.key}.title`)}
                    </h2>
                    <p className="text-base text-slate-300 leading-relaxed">
                      {t(`production_section.${step.key}.desc`)}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Banner */}
      <section className="py-16 bg-[#05060A] border-y border-white/5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex items-center gap-16 px-10">
              {['Design', 'Magnetic Core', 'LV/HV Windings', 'Drying Process', 'Assembly', 'Testing', 'Quality Control'].map((item) => (
                <span key={`${idx}-${item}`} className="text-xl font-bold text-white/10 tracking-widest uppercase">{item}</span>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
